import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import autobind from 'class-autobind';
import classNames from 'classnames';
import * as Sentry from '@sentry/browser';


// Context
import { OverviewConsumer, OverviewContext } from 'pages/Overview/OverviewContext';


// CSS
import CSS from './styles.css';


// Config
import {
	cssProperties as CSSProperties,
	exceptions as Exceptions,
	language as Lang,
	prefix
} from 'pages/Overview/config';
import { keyCodes } from 'config';


import FontSettings from 'images/icons/optimized/font-settings.svg';

// Local config
const CONFIG = {
	source: 'editor'
};


// Utilities
import { loadWOFFs } from 'utils';
import {
	checkForTab,
	classToNumber,
	clearSelection,
	convertClasslistToObject,
	editorIsEmpty,
	featuresClassName,
	fontClassName,
	fontDetailsFromClassName,
	formatBlock,
	formatSelection,
	getClassByNamespace,
	getClosest,
	getRequiredClassesFromParentEls,
	insertTextIfEmpty,
	isMetaKeyOnly,
	isParagraphEmpty,
	isTextWrappedInNode,
	logger,
	moveCaretToStartOfNode,
	normalizeParent,
	normalizeSelection,
	numberToClass,
	theCSSValue
} from 'pages/Overview/utils';


// Lodash
import {
	debounce,
	find,
	join,
	get,
	isEmpty,
	isNumber,
	toArray
} from 'lodash';


/**
 * Global variables.
 */
let selectedRange = null;
// Details around Rangy import issues:
// 		https://github.com/timdown/rangy/issues/450
import rangy from 'rangy/lib/rangy-classapplier';


let editor = null;


export default class Editor extends React.PureComponent {
	isMounted = false;

	constructor(props) {
		super(props);
		autobind(this);

		this.state = {
			activeBlock: null,
			fontSettingsTack: null,
			isMobileView: false,
			isSafari: /^((?!chrome|android).)*safari/i.test(navigator.userAgent),
			mobileBlocks: null
		};

		this.handleEditorInteractions = debounce(this.handleEditorInteractions, 50);
		document.addEventListener('keydown', this.keyboardEvents, false);

		// Refs
		this.Editor = props.editorRef;
	}


	componentDidMount() {
		this.isMounted = true;

		if (rangy) { rangy.init(); }

		editor = document.getElementById('Editor');
	}


	async componentDidUpdate(prevProps) {
		const { fontsLoaded } = this.props;

		const {
			putSetting,
			settings: {
				otFeatures,
				selectedStyle,
				stylisticSets
			},
			updateLayout
		} = this.context;

		if (!prevProps.fontsLoaded && fontsLoaded) {
			putSetting({ type: 'SET_EDITOR_IS_READY', value: true });
		}

		if (prevProps.fontsLoaded && !fontsLoaded) {
			if (this.Editor.current) {
				this.Editor.current.innerHTML = '';
			}
		}

		if (updateLayout) {
			await putSetting({ type: 'UPDATE_LAYOUT', value: false });
		}

		if (!prevProps.layoutIsInjected && this.props.layoutIsInjected) {
			global.scribe = new Scribe(this.Editor.current, { // eslint-disable-line no-undef
				allowBlockElements: true
			});

			// For some reason `onPaste` is not working when added directly to the
			// editor div, so we’re using the good old-fashioned addEventListener
			// this.Editor.current.addEventListener('paste', this.handlePaste);
			if (this.Editor.current) {
				this.Editor.current.addEventListener('paste', event => event.preventDefault());
			}
		}

		// Switch mobile view on when within the `ltLargeMedium` breakpoint.
		if (
			process.env.HAS_MOBILE_EDITOR
			&& prevProps.activeLayoutType !== this.props.activeLayoutType
			&& this.props.activeLayoutType === 'mobile'
		) {
			this.setupMobile();
		}

		// Switch mobile view off when above the `ltLargeMedium` breakpoint.
		if (
			prevProps.activeLayoutType !== this.props.activeLayoutType
			&& this.props.activeLayoutType === 'desktop'
		) {
			this.tearDownMobile();
		}


		/**
		 * PROCESS THE SELECTED CONTENT
		 *
		 * Check for changes in the store. If a change to a setting is detected,
		 * run the corresponding method for updating the selection in the Editor.
		 *
		 * =====================================================================
		 */
		const prevSettings = get(prevProps, 'immutableState.settings', null);
		const settings = get(this.props, 'immutableState.settings', null);

		// Alignment
		if (
			settings.alignment !== CONFIG.source
			&& prevSettings.alignment !== settings.alignment
			&& get(settings, 'alignment.source') !== CONFIG.source
		) {
			this.handleAlignmentChange(settings.alignment.value);
		}

		// Size
		if (
			settings.size !== CONFIG.source
			&& prevSettings.size !== settings.size
			&& get(settings, 'size.source') !== CONFIG.source
		) {
			this.handleSettingChange({ type: 'size', value: settings.size.value });
		}

		// Leading
		if (
			settings.leading !== CONFIG.source
			&& prevSettings.leading !== settings.leading
			&& get(settings, 'leading.source') !== CONFIG.source
		) {
			this.handleSettingChange({ type: 'leading', value: settings.leading.value });
		}

		// Tracking
		if (
			settings.tracking !== CONFIG.source
			&& prevSettings.tracking !== settings.tracking
			&& get(settings, 'tracking.source') !== CONFIG.source
		) {
			this.handleSettingChange({ type: 'tracking', value: settings.tracking.value });
		}

		// Paragraph Margin
		if (
			settings.pm !== CONFIG.source
			&& prevSettings.pm !== settings.pm
			&& get(settings, 'pm.source') !== CONFIG.source
		) {
			this.handleBlockChange({ type: 'pm', value: settings.pm.value });
		}

		// OpenType Features and Stylistic Sets
		if (
			(
				otFeatures.source !== CONFIG.source &&
				otFeatures !== undefined &&
				otFeatures.value !== undefined &&
				prevSettings.otFeatures !== undefined &&
				otFeatures.value !== prevSettings.otFeatures.value
			) || (
				stylisticSets.source !== CONFIG.source &&
				stylisticSets !== undefined &&
				stylisticSets.value !== undefined &&
				prevSettings.stylisticSets !== undefined &&
				stylisticSets.value !== prevSettings.stylisticSets.value
			)
		) {
			this.toggleOpenTypeFeature();
		}


		/**
		 * processDOM is a property in the selectedStyle item in the store.
		 * When updating selectedStyle, you can set processDOM: true if you
		 * want that update to be followed by an update to the selected text in
		 * the editor.
		 *
		 * This property is only attached to the selectedStyle property and not
		 * both selectedStyle and selectedFamily because the selectedStyle
		 * property changes whether or not you make a selection in the Family or
		 * Style menus. A change to Family means an automatic change to Styles.
		 *
		 * =====================================================================
		 */
		if (get(selectedStyle, 'processDOM', false)) {
			await putSetting({ type: 'SET_DOM_PROCESSED', whichType: 'style' });

			logger({
				enabled: false,
				label: 'EDITOR: processDOM: style',
				lines: [{
					label: 'whichType',
					data: 'style'
				}, {
					label: 'style',
					data: selectedStyle
				}]
			});

			this.handleFontChange();
		}
	}

	componentWillUnmount() {
		this.isMounted = false;

		const { isMobileView } = this.state;

		if (isMobileView) {
			this.tearDownMobile();
		}

		if (!isMobileView) {
			this.Editor.current.removeEventListener('paste', this.handlePaste);
		}

		document.removeEventListener('keydown', this.keyboardEvents);
	}


	async setupMobile() {
		this.setState({
			isMobileView: true,
			mobileBlocks: this.Editor.current.children
		});

		this.Editor.current.addEventListener('click', this.selectMobileBlock, false);

		if (global.scribe && global.scribe.el) {
			global.scribe.el.contentEditable = false;
		}
	}


	async tearDownMobile() {
		this.setState({
			activeBlock: null,
			fontSettingsTack: null,
			isMobileView: false,
			mobileBlocks: null
		});

		this.Editor.current.removeEventListener('click', this.selectMobileBlock, false);

		await this.context.putSetting({ type: 'TOGGLE_EDITOR_STATE', isEditable: true });

		if (global.scribe && global.scribe.el) {
			global.scribe.el.contentEditable = true;
		}
	}


	toggleFontMenu() {
		const { activePanel, putSetting } = this.context;
		putSetting({ type: 'SET_ACTIVE_PANEL', panel: activePanel === 'font' ? null : 'font' });
	}


	/* eslint-disable no-magic-numbers */
	closeOpenMenus() {
		const { activeBlock } = this.state;
		const { putSetting } = this.context;

		const blockDimensions = activeBlock.getBoundingClientRect();
		const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
		const destination = blockDimensions.y + scrollTop - 82 - 24; // eslint-disable-line no-magic-numbers

		window.scrollTo(0, destination);

		putSetting({ type: 'SET_ACTIVE_PANEL', panel: null });
	}
	/* eslint-enable no-magic-numbers */


	/* eslint-disable no-magic-numbers */
	tackFontSettings(activeBlock) {
		const blockDimensions = activeBlock.getBoundingClientRect();

		const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
		const top =
			blockDimensions.y
			- 84 // subtract height of site header and breadcrumbs
			+ ((blockDimensions.height * 0.85) / 2) // .85 attempts to account for actual vs. visual height of activeBlock
			- (30 / 2)
			+ scrollTop; // add scroll amount so the icon doesn’t appear offscreen

		const tack = (
			<div
				className={ CSS.fontSettingsTack }
				onClick={ this.toggleFontMenu }
				role="presentation"
				style={ { top } }
			>
				<FontSettings />
			</div>
		);

		this.setState({
			fontSettingsTack: tack
		});
	}
	/* eslint-enable no-magic-numbers */


	async selectMobileBlock(event) {
		if (!isEmpty(this.props.error)) { return false; }

		event.preventDefault();

		const { putSetting } = this.context;
		const { mobileBlocks } = this.state;

		// Search for the paragraph that was clicked.
		let activeBlock = find(mobileBlocks, block => block === event.target);

		// Check to see if the focused item is a paragraph. If not, crawl up the
		// DOM to find the closest <p/> element. (This is to cover the case
		// where the visitor clicked on a <span/> tag.) If no <p/> tag is found,
		// stop the operation.
		const blockIsSpan = event.target.nodeName.toLowerCase() === 'span';
		if (blockIsSpan) {
			const parentParagraph = event.target.closest('p');
			activeBlock = parentParagraph;
		}

		// If we’re inside the active paragraph, exit and return true;
		if (activeBlock === this.state.activeBlock) { return true; }

		// Do we currently have a paragraph in focus mode?
		const inFocusMode = this.state.activeBlock;

		// Was there no paragraph found in the click, or is the clicked paragraph element empty?
		const isEmptyParagraph = !activeBlock || isParagraphEmpty(activeBlock);

		// Did the click occur within the bounds of the #Editor?
		const isWithinEditor = event.target.closest('#Editor');

		try {
			// When in focus mode.
			if (inFocusMode) {
				// clicked inside editor but not on activeBlock, has active focused block
				if (isWithinEditor && this.state.activeBlock !== activeBlock) {
					this.deselectMobileBlock();
				}
			} else if (!inFocusMode) {
				this.tackFontSettings(activeBlock);

				// clicked paragraph to open
				if (activeBlock && !isEmptyParagraph) {
					activeBlock.classList.add(CSS['block--isActive']);
					this.setState({ activeBlock });
				// clicked inside editor but not on paragraph, no active activeBlock
				} else if (
					isWithinEditor
					&& (!activeBlock || isEmptyParagraph)
					&& !this.state.activeBlock
				) {
					activeBlock = mobileBlocks[0];
					activeBlock.classList.add(CSS['block--isActive']);
					this.setState({ activeBlock });
				}

				activeBlock.contentEditable = true;
				requestAnimationFrame(() => {
					activeBlock.focus();
				});
				await putSetting({ type: 'SET_FOCUS', value: true });
			}
		} catch (error) {
			if (process.env.NODE_ENV === 'development' && process.env.LOG_ERRORS) {
				throw error;
			} else {
				Sentry.withScope(scope => {
					scope.setExtra('debug', false);
					Sentry.captureException(error);
				});
			}
			return false;
		}

		// Deselect any selected text within the editor
		clearSelection(this.Editor.current);

		return activeBlock;
	}


	async deselectMobileBlock() {
		const { activeBlock } = this.state;
		const { putSetting } = this.context;

		activeBlock.contentEditable = false;

		activeBlock.classList.remove(CSS['block--isActive']);

		// activeBlock.removeEventListener('focus', this.closeOpenMenus, false);

		this.setState({ activeBlock: null });

		await putSetting({ type: 'SET_FOCUS', value: false });

		return activeBlock;
	}


	/**
	 * Keyboard events to change the state of UI elements.
	 * Used primarily for closing LicenseBar detail views.
	 *
	 * @method  keyboardEvents
	 * @param   {Object}  event  Event sent from keyboard input
	 */
	keyboardEvents(event) {
		event.stopPropagation();

		// ESC
		if (
			event.keyCode === keyCodes.ESCAPE
			&& this.state.activeBlock
		) {
			this.deselectMobileBlock();
		}
	}


	/**
	 * Update/Create selected content’s classes based on values from the store.
	 *
	 * When changing a setting within <Controls /> the new value for that
	 * setting is updated directly in the store. The <Editor /> hears those
	 * changes and responds with `handle____` the methods below.
	 *
	 * =====================================================================
	 */

	/**
	 * Toggle individual OpenType features
	 */
	toggleOpenTypeFeature() {
		if (!this.context.interactionInitiated) { return false; }

		const { activeLayoutType, immutableState: state, sheets } = this.props;
		const { isMobileView } = this.state;

		const sheet = sheets[activeLayoutType];

		const {
			otFeatures,
			stylisticSets
		} = this.context.settings;

		const theClassObject = featuresClassName(otFeatures, stylisticSets);
		const theClass = theClassObject.className;

		// Join all feature settings together to create the actual value
		// for font-feature-settings.
		const cssValue = join(theClassObject.features, ', ');

		// Inject CSS class into the stylesheet
		if (!sheet.getRule(theClass)) {
			sheet.addRule(theClass, {
				'font-feature-settings': cssValue
			}, { named: true });
		}

		if (state.initialSetupDone) {
			global.scribe.transactionManager.run(() => {
				if (isMobileView) {
					formatBlock('features', theClass);
				} else {
					formatSelection('features', theClass);
				}
			});
		}

		normalizeSelection(this.context.settings);
		return true;
	}


	/**
 	 * Handle Blur
 	 * Reverse the actions we took for `handleOnFocus`.
 	 */
	async handleOnBlur() {
		if (this.state.isMobileView) { return false; }

		const { putSetting } = this.context;

		// Grab the selection with Rangy
		const theSelection = rangy.getSelection();
		const isSelected = !theSelection.isCollapsed;
		const selLength = !!theSelection.toString().length;

		// Tell the Editor store whether or not text is selected
		await putSetting({ type: 'IS_TEXT_SELECTED', value: isSelected });

		// Tell the Editor store how many characters are selected
		await putSetting({ type: 'SELECTION_LENGTH', value: selLength });

		// Update the store with the focus state of the editor
		await putSetting({ type: 'SET_FOCUS', value: false });

		return true;
	}


	/**
	 * Handle Focus
	 * When bringing focus to the editor, we’ll need to close all menus and
	 * set `isFocused` to true.
	 */
	async handleOnFocus() {
		const { putSetting, settings } = this.context;
		const { isMobileView } = this.state;

		if (isMobileView) { return false; }

		if (!settings.interactionInitiated && !isMobileView) {
			await putSetting({ type: 'INTERACTION_INITIATED', value: true });
		}

		await putSetting({ type: 'SET_FOCUS', value: true });

		return true;
	}


	/**
	 * Update alignment for the selection when detecting a change in the store.
	 *
	 * @method handleAlignmentChange
	 * @param  {String}              alignment  left|center|right
	 * @return {Void}
	 */
	handleAlignmentChange(alignment) {
		if (!this.context.interactionInitiated) { return false; }

		const { activeLayoutType, immutableState: state, sheets } = this.props;
		const sheet = sheets[activeLayoutType];

		// The CSS class we'll be creating

		const theClass = `${prefix.alignment}${alignment}`;

		if (!sheet.getRule(theClass)) {
			// Create CSS class and inject into the stylesheet
			sheet.addRule(theClass, {
				'text-align': alignment
			}, { named: true });
		}

		if (state.initialSetupDone) {
			global.scribe.transactionManager.run(() => {
				formatBlock('alignment', theClass);
			});
		}

		normalizeSelection(this.context.settings);
		return true;
	}


	/**
	 * Handle change at the selection level (size, leading)
	 *
	 * @param  {object} result Object passed in from select menu
	 */
	handleSettingChange({ type, value }) {
		if (!this.context.interactionInitiated) { return false; }

		const { activeLayoutType, immutableState: state, sheets } = this.props;
		const { isMobileView } = this.state;

		const sheet = sheets[activeLayoutType];

		// Validate that we’re working with a number
		if (isNaN(value) || !isNumber(value)) { return false; }

		// Determine which unit of measurement should be used. Size gets `vw`
		// while all others get `px`.
		const unit = type === 'size' ? 'vw' : 'em';

		// The CSS class we'll be creating
		const numToClass = type === 'size' ? numberToClass(value, '-') : numberToClass(value);
		const theClass = `${prefix[type]}${numToClass}`;

		if (!sheet.getRule(theClass)) {
			// Create CSS class and inject into the stylesheet
			sheet.addRule(theClass, {
				[CSSProperties[type]]: `${value}${unit}`
			}, { named: true });
		}

		if (state.initialSetupDone) {
			if (isMobileView) {
				formatBlock(type, theClass);
			} else {
				formatSelection(type, theClass);
			}
		}

		normalizeSelection(this.context.settings);
		return true;
	}


	/**
	 * Handle change at the block level (leading, paragraph margins)
	 *
	 * @param  {object} result Object passed in from select menu
	 */
	handleBlockChange({ type, value }) {
		if (!this.context.interactionInitiated) { return false; }

		const { activeLayoutType, immutableState: state, sheets } = this.props;
		const sheet = sheets[activeLayoutType];

		// The CSS class we'll be creating
		const numToClass = numberToClass(value);
		const theClass = `${prefix[type]}${numToClass}`;

		if (!sheet.getRule(theClass)) {
			// Create CSS class and inject into the stylesheet
			sheet.addRule(theClass, {
				[CSSProperties[type]]: `${value}em`
			}, { named: true });
		}

		if (state.initialSetupDone) {
			global.scribe.transactionManager.run(() => {
				formatBlock(type, theClass);
			});
		}

		normalizeSelection(this.context.settings);
		return true;
	}


	checkEditorEvent(event) { // eslint-disable-line consistent-return
		if (
			!isEmpty(this.props.error)
			|| isMetaKeyOnly(event)
			|| editorIsEmpty(event, this.Editor.current)
		) { return false; }
	}


	/**
	 * Handle Editor Interactions
	 * Primary function for handling all interactions in the editor. Should
	 * always first run checks to see if anything actually has to change, and
	 * only update the store when absolutely necessary.
	 */
	async handleEditorInteractions(event) {
		if (
			!isEmpty(this.props.error)
			|| isMetaKeyOnly(event)
			|| editorIsEmpty(event, this.Editor.current)
		) { return false; }

		const { products } = this.props;

		const {
			editorContent,
			editorIsDirty,
			editorIsFocused,
			putSetting,
			settings
		} = this.context;

		const { isMobileView } = this.state;

		if (!editorIsFocused) { return false; }

		// Check for a keyDown of the TAB key. If detected, replace with spaces.
		checkForTab(event, 'Editor');

		if (
			!editorIsDirty
			&& !isEmpty(editorContent)
			&& editorContent.length !== this.Editor.current.innerHTML.length
		) {
			await putSetting({ type: 'SET_EDITOR_IS_DIRTY', value: true });
		}

		if (editorContent !== this.Editor.current.innerHTML) {
			await putSetting({
				type: 'UPDATE_EDITOR_CONTENT',
				content: this.Editor.current.innerHTML,
				silent: false
			});
		}

		// Grab the selection with Rangy
		const theSelection = rangy.getSelection();

		// Set the state of the text selection size
		putSetting({ type: 'SELECTION_COLLAPSED', value: theSelection.isCollapsed });

		// Check previous range against current one.
		// If they’re no different, we can stop here.
		if (
			selectedRange !== null &&
			!theSelection.isCollapsed &&
			theSelection.getRangeAt(0) &&
			rangy.DomRange.rangesEqual(selectedRange, theSelection.getRangeAt(0))
		) {
			return false;
		}

		selectedRange = theSelection.getRangeAt(0);

		// Get all <p> and <span> nodes from the selection
		const selectedNodes =
			isMobileView
				? 0
				: selectedRange.getNodes([1], node =>
					node.nodeName.toLowerCase() === 'p' || node.nodeName.toLowerCase() === 'span'
				);

		// We’ve run all checks above.
		// Now let’s perform the requested action!

		let theAlignment;
		let theFamily;
		let theFamilyID;
		let theFeatures;
		let theLeading;
		let thePM;
		let theSize;
		let theStyle;
		let theStyleSource;
		let theTracking;

		// If selecting across more than one node…
		if (
			selectedNodes.length > 0 &&
			theSelection.getRangeAt(0).endContainer.nodeName.toLowerCase() !== 'p'
		) {
			const classLists = [];

			// Get the parent node
			// If the selected parentNode is not a <p/> element, travel up the
			// DOM until we reach the <p/> element.
			let theParentNode = theSelection.anchorNode.parentElement;
			if (theParentNode.nodeName.toLowerCase() !== 'p') {
				theParentNode = getClosest(theParentNode, 'p');
			}

			selectedNodes.push(theParentNode);

			// Iterate over selectedNodes and gather classes
			selectedNodes.forEach(node => {
				const classList = toArray(node.classList);
				classLists.push(classList);
			});

			// Take all gathered classes and convert them to a key/value
			// pair object
			const classesInSelection = convertClasslistToObject(classLists);

			// If font details exist, update the font menu
			theFamily = get(classesInSelection, 'fonts.length') === 1 ? classesInSelection.fonts[0] : false;
			theStyle = get(classesInSelection, 'styles.length') === 1 ? classesInSelection.styles[0] : false;

			const {
				alignment,
				features,
				leading,
				pm,
				size,
				tracking
			} = classesInSelection;


			theAlignment = alignment;
			theFeatures = features;
			theLeading = isNaN(leading) ? false : theCSSValue(leading);
			thePM = isNaN(pm) ? false : theCSSValue(pm);
			theSize = isNaN(size) ? false : theCSSValue(size);
			theTracking = isNaN(tracking) ? false : theCSSValue(tracking);

			logger({
				color: 'red',
				enabled: false,
				label: 'handleBlockChange',
				lines: [{
					label: 'classesInSelection',
					data: classesInSelection
				}, {
					label: 'leading',
					data: leading
				}, {
					label: 'pm',
					data: pm
				}, {
					label: 'size',
					data: size
				}, {
					label: 'tracking',
					data: tracking
				}, {
					label: 'theLeading',
					data: theLeading
				}, {
					label: 'thePM',
					data: thePM
				}, {
					label: 'theSize',
					data: theSize
				}, {
					label: 'theTracking',
					data: theTracking
				}],
				padding: [5, 5] // eslint-disable-line no-magic-numbers
			});
		// One or fewer (blinking caret) nodes selected
		} else {
			// Get the closest node for the selected text. If the text content
			// of selection matches the closest childNode, select that. If the
			// text matched the text content of the closest parentElement,
			// select that. Otherwise, select the parentElement by default.
			const theParentNode = isTextWrappedInNode(theSelection);

			const classList = getRequiredClassesFromParentEls(theParentNode);

			// Fail with a warning when we have issue getting the classList
			// from the selected element.
			if (!classList) { return false; }

			// Which font are we focused on?
			const theFocusedFont = getClassByNamespace(prefix.font, classList);

			if (theFocusedFont) {
				const fontDetails = fontDetailsFromClassName(theFocusedFont);
				theFamily = fontDetails[1];
				const familyRecords = find(products, item => item.attributes.name.replace(Exceptions.nitroTurbo, '') === theFamily.replace(/-/g, ' '));
				theFamilyID = parseInt(familyRecords.id, 10);
				theStyle = parseInt(fontDetails[2], 10);

				logger({
					enabled: false,
					label: 'UPDATE FAMILY AND STYLE: No nodes selected',
					lines: [{
						label: 'theFocusedFont',
						data: theFocusedFont
					}, {
						label: 'theFamily',
						data: theFamily
					}, {
						label: 'familyRecords',
						data: familyRecords
					}, {
						label: 'theFamilyID',
						data: theFamilyID
					}, {
						label: 'theStyle',
						data: theStyle
					}]
				});
			}


			// Which leading are we focused on?
			const theFocusedLeading = getClassByNamespace(prefix.leading, classList);
			if (theFocusedLeading) {
				const leading = theFocusedLeading.replace(prefix.leading, '');
				theLeading = theCSSValue(leading);
			}

			// Which paragraph margin are we focused on?
			const theFocusedPM = getClassByNamespace(prefix.pm, classList);
			if (theFocusedPM) {
				const pm = theFocusedPM.replace(prefix.pm, '');
				thePM = theCSSValue(pm);
			}

			// Which size are we focused on?
			const theFocusedSize = getClassByNamespace(prefix.size, classList);
			if (theFocusedSize) {
				const size = theFocusedSize.replace(prefix.size, '');
				theSize = classToNumber(size);
			}

			// Which tracking are we focused on?
			const theFocusedTracking = getClassByNamespace(prefix.tracking, classList);
			if (theFocusedTracking) {
				const tracking = theFocusedTracking.replace(prefix.tracking, '');
				theTracking = theCSSValue(tracking);
			}

			// Which alignment are we focused on?
			const theFocusedAlignment = getClassByNamespace(prefix.alignment, classList);
			if (theFocusedAlignment) {
				const alignment = theFocusedAlignment.replace(prefix.alignment, '');
				theAlignment = alignment;
			}

			// Which features are we focused on?
			const theFocusedFeatures = getClassByNamespace(prefix.features, classList);
			if (theFocusedFeatures) {
				const features = theFocusedFeatures.replace(prefix.features, '');
				theFeatures = features.replace(/-\s*$/, '');
			}
		}

		// Fire off store actions
		if (theAlignment === undefined) {
			theAlignment = 'left';
		}

		if (theAlignment) {
			await putSetting({ type: 'ALIGNMENT', value: theAlignment, silent: true, source: CONFIG.source });
		}

		/**
		 * Update font family based on activity in the Editor (mostly moving the
		 * caret around or clicking into the editor).
		 *
		 * =====================================================================
		 */
		logger({
			enabled: false,
			label: 'COME OUT OF NULL',
			lines: [{
				label: 'theFamily',
				data: theFamily
			}, {
				label: 'selectedFamily',
				data: this.context.settings.selectedFamily
			}, {
				label: 'theStyle',
				data: theStyle
			}, {
				label: 'source',
				data: CONFIG.source
			}, {
				label: 'theFamilyID && !this.context.settings.selectedFamily',
				data: theFamilyID && !this.context.settings.selectedFamily
			}]
		});

		if (
			!get(this.context, 'settings.selectedFamily')
			|| this.context.settings.selectedFamily
			&& theFamily !== get(this.context, 'settings.selectedFamily.label')
		) {
			logger({
				enabled: false,
				label: 'EDITOR: SET_FAMILY_BY_NAME',
				lines: [{
					label: 'theFamily',
					data: theFamily
				}, {
					label: 'source',
					data: CONFIG.source
				}]
			});

			await putSetting({ type: 'SET_FAMILY_BY_NAME', name: theFamily, silent: true, source: CONFIG.source });
		}

		if (
			// theStyle
			// && this.context.settings.selectedStyle
			theStyle !== get(this.context, 'settings.selectedStyle.id')
			&& (theStyleSource === CONFIG.source || !theStyleSource)
		) {
			logger({
				enabled: false,
				label: 'EDITOR: SET_STYLE',
				lines: [{
					label: 'id',
					data: theStyle
				}, {
					label: 'familyID',
					data: theFamilyID
				}, {
					label: 'source',
					data: CONFIG.source
				}, {
					label: 'prevSelectedStyle',
					data: this.context.settings.prevSelectedStyle
				}, {
					label: 'prevSelectedFamily',
					data: this.context.settings.prevSelectedFamily
				}]
			});

			await putSetting({ type: 'SET_STYLE', familyID: theFamilyID, styleID: theStyle, silent: true, source: CONFIG.source });
		}

		if (theFeatures) {
			await putSetting({ type: 'SET_OPENTYPE_FEATURES', value: theFeatures, silent: true, source: CONFIG.source });
		}

		if (theLeading) {
			await putSetting({ type: 'LEADING', value: theLeading, silent: true, source: CONFIG.source });
		}

		if (theSize) {
			await putSetting({ type: 'SIZE', value: theSize, silent: true, source: CONFIG.source });
		}

		// We need to use `isNumber()` on the following two because their base
		// values are “0”, which will read as `false` when using `if (thePM)`
		if (isNumber(thePM) || thePM === Lang.multipleStylesSelected) {
			await putSetting({ type: 'PM', value: thePM, silent: true, source: CONFIG.source });
		}

		if (isNumber(theTracking) || theTracking === Lang.multipleStylesSelected) {
			await putSetting({ type: 'TRACKING', value: theTracking, silent: true, source: CONFIG.source });
		}

		// Tell the Editor store whether or not text is selected
		await putSetting({ type: 'IS_TEXT_SELECTED', value: !theSelection.isCollapsed, silent: true });

		// Tell the Editor store how many characters are selected
		await putSetting({ type: 'SELECTION_LENGTH', selection: theSelection.toString().length, silent: true });

		// Store the position of the caret in the editor
		await putSetting({ type: 'CARET_POSITION', anchor: theSelection.anchorOffset, offset: theSelection.focusOffset });

		normalizeParent(settings);

		return true;
	}

	async handleFontChange() {
		if (!this.context.interactionInitiated) { return false; }

		const {
			activeBlock,
			isMobileView
		} = this.state;

		const {
			editorContent,
			editorIsFocused,
			initialSetupDone,
			putSetting,
			settings: {
				prevSelectedFamily,
				selectedFamily,
				selectedStyle
			}
		} = this.context;

		const selection = rangy.getSelection();
		const isSelectionCollapsed = selection.isCollapsed;

		logger({
			enabled: false,
			label: 'EDITOR: handleFontChange()',
			lines: [{
				label: 'previous family',
				data: prevSelectedFamily
			}, {
				label: 'current family',
				data: selectedFamily
			}]
		});

		const { activeLayoutType, addRules, sheets } = this.props;

		// Set the stylesheet that we’ll be writing to.
		const sheet = sheets[activeLayoutType];

		// Stop here if the visitor hasn’t selected a style yet
		// Not terribly relevant anymore, but I’ll leave it as an added check.
		// if (!selectedFamily || selectedFamily === null || selectedStyle === null) { return false; }

		// If there is no text selected (just a blinking caret) we’ll need to
		// inject text (by default, the style’s name) and select it for
		// transformation. When selecting the text we need to determine if the
		// caret is currently in an empty <p/> tag. If not, we’ll create a text
		// node and inject the text.
		let didInsertText;
		if (selectedFamily.label && !isMobileView && !isSelectionCollapsed) {
			didInsertText = insertTextIfEmpty(editorIsFocused, selectedFamily.label);
		}

		// Create the CSS based on the product data
		const fontStyles = {
			'font-family': `${selectedStyle.value}-TDB, ${selectedStyle.value}-TDA, Fallback, Courier`,
			'font-style': 'normal',
			'font-weight': 'normal'
		};

		// Create CSS class
		const theClass = fontClassName(selectedFamily.label, selectedStyle.id);

		if (!sheet.getRule(theClass)) {
			// Add the CSS to the JSS sheet
			sheet.addRule(theClass, fontStyles, { named: true });
		}

		const rules = [
			[
				'@font-face',
				['font-family', `${selectedStyle.value}-TDB`],
				['font-weight', 'normal'],
				['font-style', 'normal'],
				['src', `url(/overview/fonts/woff/${selectedStyle.value}-TD-Space.woff)`]
			], [
				'@font-face',
				['font-family', `${selectedStyle.value}-TDA`],
				['font-weight', 'normal'],
				['font-style', 'normal'],
				['src', `url(/overview/fonts/woff/${selectedStyle.value}-TD.woff)`]
			]
		];

		// Add the generated CSS rules to the local StyleSheet.
		addRules(rules);

		if (isMobileView && activeBlock) {
			activeBlock.focus();
			moveCaretToStartOfNode(activeBlock);
		}

		if (initialSetupDone) {
			global.scribe.transactionManager.run(() => {
				if (isMobileView || isSelectionCollapsed) {
					formatBlock('font', theClass);
				} else {
					formatSelection('font', theClass, !!didInsertText);
				}
			});
		}

		// Finally, update editor content if it has changed
		if (editorContent !== editor.innerHTML) {
			await putSetting({ type: 'UPDATE_EDITOR_CONTENT', content: editor.innerHTML, source: CONFIG.source });
		}

		const woffs = [
			`${selectedStyle.value}-TDB`,
			`${selectedStyle.value}-TDA`
		];

		if (this.isMounted) {
			await loadWOFFs(woffs);
		}

		normalizeSelection(this.context.settings);
		return true;
	}


	render() {
		const { activeBlock, fontSettingsTack, isMobileView, isSafari } = this.state;
		const { editorIsReady } = this.context;

		return (
			<OverviewConsumer>
				{ () =>
					<Fragment>
						{ isMobileView && fontSettingsTack }
						<div
							autoCapitalize="none"
							autoComplete="false"
							autoCorrect="false"
							className={
								classNames(
									CSS.overview, {
										[CSS['overview--isReady']]: editorIsReady,
										[CSS['overview--isActive']]: activeBlock,
										[CSS['overview--isSafari']]: isSafari
									}
								)
							}
							id="Editor"
							onBlur={ this.handleOnBlur }
							onDragStart={ event => event.preventDefault() }
							onFocus={ this.handleOnFocus }
							onKeyDown={ event => { event.persist(); this.handleEditorInteractions(event); } }
							onMouseDown={ event => { event.persist(); this.handleEditorInteractions(event); } }
							onMouseUp={ event => { event.persist(); this.handleEditorInteractions(event); } }
							ref={ this.Editor }
							role="textbox"
							spellCheck="false"
							tabIndex={ 0 }
						/>
					</Fragment>
				}
			</OverviewConsumer>
		);
	}
}

Editor.contextType = OverviewContext;

Editor.propTypes = {
	activeLayoutType: PropTypes.string,
	addRules: PropTypes.func.isRequired,
	editorRef: PropTypes.object.isRequired,
	error: PropTypes.oneOfType([
		PropTypes.bool,
		PropTypes.shape({ type: PropTypes.string, message: PropTypes.string })
	]),
	fontsLoaded: PropTypes.bool,
	products: PropTypes.array,
	immutableState: PropTypes.object.isRequired, // eslint-disable-line react/no-unused-prop-types
	sheets: PropTypes.object
};

Editor.defaultProps = {
	activeLayoutType: null,
	error: null,
	fontsLoaded: false,
	products: null,
	sheets: null
};
